// Dual-mode plan-modification helper.
//
// Wraps every plan-generating / plan-modifying POST so the call sites
// don't have to care whether the backend answered synchronously (200 +
// {success, plan}) or asynchronously (202 + {status:'processing', job_id}).
// The helper:
//
//   1. Fires plan-regen-start so the global banner in App.js shows up.
//   2. Makes the POST.
//   3. If the response is 202 with a job_id, polls GET /plan-status/{id}
//      every POLL_INTERVAL_MS until status leaves 'processing'.
//   4. If the response is 200 with success=true, returns immediately.
//   5. Fires plan-regen-done (success / fail) so the banner closes out
//      the right colour.
//
// Call sites can do:
//
//     const result = await planRegenRequest({
//       url: `${API_BASE_URL}/weekly-plan/generate-v2?user_id=${uid}`,
//       body: { week_type, sport_type },
//       pollUserId: uid,
//       changedFields: ['תכנית ריצה — שבוע נוכחי'],
//     });
//     if (result.success) { ... } else { showToast(result.error, 'error'); }
//
// Important: the helper does NOT auto-toast or auto-alert on error —
// the caller decides how to surface failures. That keeps it usable in
// flows that have their own per-task error UI (e.g. WeeklyPlan's
// red banner) without double-notifying.

import authFetch from './authFetch';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

// Frontend polling budget — slightly longer than the backend's
// STALE_AFTER_SECONDS (360s) so a healthy job that runs right up to
// the wire still resolves rather than getting a polling_timeout.
const POLL_INTERVAL_MS = 3000;
// Single-plan default — was raised back from 3.5min to 4min
// (2026-06-05): 3.5min was too tight when a single plan retried
// after a transient 502 (~60s base + 5s wait + 60s retry = 125s,
// add validator overhead and we're at 150s+).
// Batch / regenerate-all should override via the maxPollMs option
// (passes ~7 min since 4 plans × ~75s = 300s, plus up to one
// 65-second retry on each iteration = ~430s worst case).
const DEFAULT_MAX_POLL_TIME_MS = 4 * 60 * 1000;
const BATCH_MAX_POLL_TIME_MS = 7 * 60 * 1000;

/**
 * Run a dual-mode plan-modifying request.
 *
 * @param {Object} opts
 * @param {string} opts.url            POST URL (already includes any query string)
 * @param {Object} [opts.body]         Request body — JSON.stringified before send
 * @param {string} opts.pollUserId     user_id to pass to /plan-status (permission check)
 * @param {Function} [opts.statusUrl]  jobId -> the url to poll instead (the coach: /coach/athlete/{id}/plan-status)
 * @param {string[]} [opts.changedFields]  Labels for the banner (e.g. ['ק"מ שבועי'])
 * @param {boolean} [opts.silent]      When true, don't fire plan-regen-start /
 *                                     plan-regen-done. Use this in batch
 *                                     flows that need to dispatch ONE pair
 *                                     for the whole batch (e.g. Profile's
 *                                     running+cycling × current+next loop).
 * @param {number}  [opts.maxPollMs]   Override the polling budget. Defaults
 *                                     to DEFAULT_MAX_POLL_TIME_MS (4 min)
 *                                     for single-plan calls. Pass
 *                                     BATCH_MAX_POLL_TIME_MS (7 min) for
 *                                     /weekly-plan/regenerate-all — the
 *                                     server loops 4 sub-generations and
 *                                     can hit 5-6 min total with one retry.
 * @returns {Promise<{
 *   success: boolean,
 *   status?: 'completed' | 'failed',
 *   plan?: Object,         // populated on sync 200 path
 *   plan_id?: number,
 *   result_meta?: Object,  // populated on async polling path (light metadata only)
 *   error?: string,
 *   // …pass-through fields from the soft-error sync response (limit_reached, blocked, etc.)
 * }>}
 */
export async function planRegenRequest({ url, body, pollUserId, changedFields = [], silent = false, maxPollMs,
                                         statusUrl }) {
  if (!silent) {
    window.dispatchEvent(new CustomEvent('plan-regen-start', {
      detail: { changedFields },
    }));
  }
  // Track whether we dispatched start so we know whether to fire
  // a matching 'done' (or 'skipped') at the end. Banner UX:
  // - completed → green "done"
  // - skipped (no_op) → blue "nothing to do" toast (no green banner)
  // - failed → red error
  // Global flag for cross-component coordination (2026-06-07). WeeklyPlan
  // checks this before kicking off its own bgSync — without it, the
  // per-task generate-v2 calls fire alongside our batch and produce
  // premature 'ready' toasts. Set even when silent=true so onboarding
  // and profile-driven flows both coordinate.
  try { window.__planRegenInFlight = true; } catch {}
  // Resolve polling budget. If the caller didn't pick, infer from the URL —
  // /regenerate-all is the batch endpoint and needs the longer budget.
  const _effectiveMaxPollMs = (
    typeof maxPollMs === 'number' && maxPollMs > 0
      ? maxPollMs
      : (url && url.includes('/weekly-plan/regenerate-all')
          ? BATCH_MAX_POLL_TIME_MS
          : DEFAULT_MAX_POLL_TIME_MS)
  );
  let success = false;
  try {
    const res = await authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    let data;
    try {
      data = await res.json();
    } catch (_) {
      data = {};
    }

    // ── Async path: 202 + job_id → poll
    if (res.status === 202 && data.job_id) {
      const pollStart = Date.now();
      const final = await pollUntilDone(data.job_id, pollUserId, _effectiveMaxPollMs, statusUrl);
      success = final.status === 'completed';

      // Polling-timeout recovery (added 2026-06-04 after Render
      // health-check incident). The BG worker can save a plan to
      // active_plans, then die before mark_completed runs — the job
      // stays 'processing' forever, the poll times out, and the user
      // sees an error even though their plan IS ready. Before
      // returning the error, ask the backend: is there a plan freshly
      // saved for this user since we started polling? If yes, treat
      // as success.
      // (not with a statusUrl: the rescue reads the caller's OWN plans — a coach following an athlete cannot)
      if (!success && final.error === 'polling_timeout' && !statusUrl) {
        const rescued = await tryRescueFromActivePlan(pollUserId, body, pollStart);
        if (rescued) {
          return { success: true, status: 'completed', plan: rescued.plan, plan_id: rescued.plan_id, rescued: true };
        }
      }
      return {
        success,
        status: final.status,
        plan_id: final.plan_id,
        result_meta: final.result_meta,
        error: success ? undefined : (final.error || 'unknown'),
      };
    }

    // ── Sync 200 path — happy: {success: true, plan, plan_id}
    if (res.ok && data && data.success === true) {
      // No-op case: backend skipped because plans already exist
      // (skip-existing optimization). Bug 2026-06-11: this was being
      // treated as 'completed' → green "תוכנית מוכנה" banner even
      // though no new plan was generated and nothing changed.
      if (data.status === 'no_op') {
        // Plans already exist = they're READY, not a failure. Mark success so the
        // finally block fires plan-regen-done{success:true} instead of the red
        // "יצירת תכנית נכשלה" banner. (Bug: the local `success` stayed false here, so
        // no_op — a valid outcome on re-onboarding — showed a false failure.)
        success = true;
        return {
          success: true,
          status: 'no_op',
          message: data.message,
          skipped: true,
        };
      }
      success = true;
      return {
        success: true,
        status: 'completed',
        plan: data.plan,
        plan_id: data.plan_id,
      };
    }

    // ── Sync 200 with soft error: {success: false, error, message, ...}
    if (res.ok && data && data.success === false) {
      return {
        success: false,
        status: 'failed',
        error: data.error || data.message || 'soft_fail',
        ...data,
      };
    }

    // ── HTTP error (4xx/5xx, not 202)
    return {
      success: false,
      status: 'failed',
      error:
        (data && (data.detail?.message || data.detail || data.error || data.message)) ||
        `HTTP ${res.status}`,
    };
  } catch (e) {
    return { success: false, status: 'failed', error: e?.message || 'fetch_failed' };
  } finally {
    try { window.__planRegenInFlight = false; } catch {}
    if (!silent) {
      window.dispatchEvent(new CustomEvent('plan-regen-done', {
        detail: { success },
      }));
    }
  }
}

/**
 * After a polling_timeout, check if the BG task actually saved a plan
 * before it died. If a plan for this user/sport/week exists with
 * created_at >= the time we started polling, treat the regen as
 * successful (the timeout was a tracking issue, not a real failure).
 *
 * Body shape we look at: { week_type, sport_type } — these are the
 * fields planRegenRequest callers actually pass for generate-v2.
 * Modify calls don't carry these so this just returns null.
 */
async function tryRescueFromActivePlan(pollUserId, body, pollStartMs) {
  try {
    const sportType = body && body.sport_type;
    if (!sportType) return null;
    const res = await authFetch(
      `${API_BASE_URL}/weekly-plan/all-active?user_id=${encodeURIComponent(pollUserId)}`
    );
    if (!res.ok) return null;
    const data = await res.json();
    const plans = (data && data.plans) || [];
    // Match by sport. Active week (status=active) is unique per
    // sport+week so we don't need to filter by week_type — if a plan
    // is active for THIS sport AND was created during our poll, it's
    // the plan we just generated.
    const candidates = plans.filter(p => (p.sport_type || 'running') === sportType);
    if (!candidates.length) return null;
    for (const plan of candidates) {
      const createdRaw = plan.updated_at || plan.created_at;
      if (!createdRaw) continue;
      const createdMs = new Date(createdRaw).getTime();
      if (Number.isNaN(createdMs)) continue;
      // 30s slack for clock drift between client + server.
      if (createdMs + 30_000 < pollStartMs) continue;
      return { plan, plan_id: plan.id };
    }
    return null;
  } catch (_) {
    return null;
  }
}

async function pollUntilDone(jobId, pollUserId, maxPollMs = DEFAULT_MAX_POLL_TIME_MS, statusUrl) {
  const start = Date.now();
  while (Date.now() - start < maxPollMs) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    try {
      const res = await authFetch(statusUrl
        ? statusUrl(jobId)
        : `${API_BASE_URL}/plan-status/${encodeURIComponent(jobId)}?user_id=${encodeURIComponent(pollUserId)}`);
      if (!res.ok) continue;  // transient — keep polling
      const data = await res.json();
      if (data && data.status && data.status !== 'processing') {
        return data;
      }
    } catch (_) {
      // network blip — keep polling
    }
  }
  return { status: 'failed', error: 'polling_timeout' };
}
